import { makeStyles } from '@mui/styles';
import Button from '@classify/react-core-components/Button';
import { useTheme } from '@classify/react-core-components/styles';
import { Link } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { translate } from '@openclassrooms/translations';
import Typography from '@classify/react-core-components/Typography';
import EditionPanelVariants from '../../constants/EditionPanelVariants';

const useStyles = makeStyles(({ spacing, palette, breakpoints }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: palette.background.default,
    padding: spacing(2),
    margin: spacing(4, 0),
    border: `1px solid ${palette.stroke}`,

    [breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  statusWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: 'auto',
    marginBottom: spacing(3),

    [breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginRight: '0',
      marginBottom: '0',
    },
  },
  statusLabel: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',

    '&:not(:last-child)': {
      marginBottom: spacing(2),
    },

    [breakpoints.up('sm')]: {
      textAlign: 'center',

      '&:not(:last-child)': {
        marginBottom: '0',
      },

      '&:not(:first-of-type)': {
        marginLeft: spacing(3),
      },
    },
  },
  statusIcon: {
    marginRight: spacing(1),
    width: 18,
  },
}));

export interface CourseEditionPanelProps {
  courseId?: string;
  chapterId?: string;
  exerciseId?: string;
  isPublished?: boolean;
  variant?: keyof typeof EditionPanelVariants;
}

const CourseEditionPanel = ({
  courseId,
  chapterId,
  exerciseId,
  isPublished,
  variant = EditionPanelVariants.HOMEPAGE,
}: CourseEditionPanelProps) => {
  const classes = useStyles();
  const { palette } = useTheme();

  const buttonInformation = {
    [EditionPanelVariants.HOMEPAGE]: {
      label: translate('course.chapter.admin_section.actions.edit_course'),
      href: `/courses/${courseId}/edit`,
      hasStatus: true,
    },
    [EditionPanelVariants.CHAPTER]: {
      label: translate('course.chapter.admin_section.actions.edit'),
      href: `/courses/${courseId}/parts/${chapterId}/edit`,
      hasStatus: true,
    },
    [EditionPanelVariants.QUIZ]: {
      label: translate('course.quiz.admin_section.actions.edit'),
      href: `/exercises/${exerciseId}/edit`,
      hasStatus: false,
    },
  };

  const currentUrl = new URL(window.location.href);
  const isDraftStatus = currentUrl.searchParams.get('status') === 'draft' || !isPublished;
  if (isDraftStatus) {
    currentUrl.searchParams.delete('status');
  }
  const urlWithoutDraft = currentUrl.toString();

  return (
    <div className={classes.root} data-testid="courseEditionPanel">
      {buttonInformation[variant].hasStatus && (
        <div className={classes.statusWrapper}>
          <Typography
            style={{ color: isDraftStatus ? palette.brand.main : palette.text.primary }}
            variant="subheadingSmall"
            className={classes.statusLabel}
            data-testid="unpublishedText"
            component={Link}
            href={`${urlWithoutDraft}?status=draft`}
          >
            <VisibilityIcon className={classes.statusIcon} />
            {translate('course.chapter.admin_section.status.writing_progress')}
          </Typography>

          <Typography
            style={{ color: !isDraftStatus ? palette.brand.main : palette.text.primary }}
            variant="subheadingSmall"
            className={classes.statusLabel}
            data-testid="publishedText"
            component={Link}
            href={urlWithoutDraft}
          >
            <VisibilityIcon className={classes.statusIcon} />
            {translate('course.chapter.admin_section.status.published')}
          </Typography>
        </div>
      )}

      <Button
        sx={{ marginRight: { xs: 'auto', sm: 0 }, marginLeft: { xs: 0, sm: 'auto' } }}
        component="a"
        href={buttonInformation[variant].href}
        variant="primary"
        aria-label={buttonInformation[variant].label}
      >
        {buttonInformation[variant].label}
      </Button>
    </div>
  );
};

export default CourseEditionPanel;
